import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import {
    CheckCircle,
    AlertTriangle,
    Trash2,
    X,
    Cookie,
} from "lucide-react";

// Types

type Position = "top-left" | "top-right" | "bottom-left" | "bottom-right";
type Feedback = { message: string; type: "success" | "error" };

const posClasses: Record<Position, string> = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
};

interface CookieDevToolProps {
    position?: Position;
}

export const CookieDevTool = ({ position = "bottom-right" }: CookieDevToolProps) => {
    const [open, setOpen] = useState(false);
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [cookies, setCookies] = useState<Record<string, string>>({});
    const [feedback, setFeedback] = useState<Feedback | null>(null);

    useEffect(() => {
        if (open) parseCookies();
    }, [open]);

    const isDemo = typeof window !== "undefined" && window?.location?.host.includes("github.io");
    const isProd = process.env.NODE_ENV === "production";
    if (isProd && !isDemo) return null;

    const parseCookies = () => {
        const parsed: Record<string, string> = {};
        document.cookie.split(";").forEach((cookie) => {
            const [k, ...v] = cookie.trim().split("=");
            if (k) parsed[k] = v.join("=");
        });
        setCookies(parsed);
    };

    const handleSetCookie = () => {
        if (!key) {
            setFeedback({ message: "Please provide a cookie key.", type: "error" });
            return;
        }
        document.cookie = `${key}=${value}; path=/`;
        setFeedback({ message: `Cookie "${key}" set!`, type: "success" });
        parseCookies();
        setTimeout(() => setFeedback(null), 3000);
    };

    const handleClearCookie = () => {
        if (!key) {
            setFeedback({ message: "Please provide a cookie key to clear.", type: "error" });
            return;
        }
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        setFeedback({ message: `Cookie "${key}" cleared.`, type: "success" });
        setKey("");
        setValue("");
        parseCookies();
        setTimeout(() => setFeedback(null), 3000);
    };

    const handleSelectCookie = (selectedKey: string) => {
        if (selectedKey === "__new__") {
            setKey("");
            setValue("");
        } else {
            setKey(selectedKey);
            setValue(cookies[selectedKey] || "");
        }
    };

    const handleClearAllCookies = () => {
        if (Object.keys(cookies).length === 0) {
            setFeedback({ message: "No cookies to clear.", type: "error" });
            return;
        }
        Object.keys(cookies).forEach((cookieKey) => {
            document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        setCookies({});
        setKey("");
        setValue("");
        setFeedback({ message: "All cookies cleared.", type: "success" });
        setTimeout(() => setFeedback(null), 3000);
    };

    return (
        <div className={`fixed z-50 ${posClasses[position]} transition-all`}>
            {!open && (
                <Button
                    variant="outline"
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-2"
                >
                    <Cookie size={16} /> Cookies
                </Button>
            )}

            {open && (
                <div className="relative mt-2 w-80 p-5 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-2xl transition-all backdrop-blur-sm text-zinc-900 dark:text-zinc-100">
                    <h3 className="text-lg font-semibold">Manage Cookies</h3>

                    {/* Panel close button */}
                    <button
                        onClick={() => setOpen(false)}
                        className="absolute top-3 right-3 text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white transition w-8 h-8 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                        aria-label="Close panel"
                    >
                        <X size={16} />
                    </button>

                    {/* Feedback */}
                    {feedback && (
                        <div
                            className={`mt-2 relative flex items-start gap-2 text-sm px-4 py-3 rounded-md border transition-colors ${feedback.type === "success"
                                ? "bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700"
                                : "bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700"
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                {feedback.type === "success" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                <span>{feedback.message}</span>
                            </div>
                            <button
                                onClick={() => setFeedback(null)}
                                className="absolute right-2 top-2 text-inherit opacity-60 hover:opacity-100 transition w-6 h-6 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                                aria-label="Dismiss feedback"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    )}

                    {/* Form */}
                    <div className="space-y-3 mt-4">
                        {Object.keys(cookies).length > 0 && (
                            <div className="space-y-1">
                                <Label>Select Cookie</Label>
                                <Select onValueChange={handleSelectCookie} value={key || "__new__"}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a cookie" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__new__">+ New Cookie</SelectItem>
                                        {Object.entries(cookies).map(([cookieKey]) => (
                                            <SelectItem key={cookieKey} value={cookieKey}>
                                                {cookieKey}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-1">
                            <Label htmlFor="cookie-key">Key</Label>
                            <Input
                                id="cookie-key"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="e.g. session_token"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="cookie-value">Value</Label>
                            <Input
                                id="cookie-value"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="e.g. abc123"
                            />
                        </div>

                        <div className="flex flex-col gap-2 pt-2">
                            <Button className="w-full" onClick={handleSetCookie}>
                                Set Cookie
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={handleClearCookie}
                                disabled={!key}
                            >
                                Clear Cookie
                            </Button>
                            <Button
                                variant="destructive"
                                className="w-full flex items-center justify-center gap-2"
                                onClick={handleClearAllCookies}
                            >
                                <Trash2 size={16} />
                                Clear All Cookies
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
